import {useEffect, useState} from "react";
import {PhoneHQLoader} from 'ch3-ui-lib';
import {RotateCcw} from "lucide-react";
import {useListEnsAlertsQuery} from "../../../store/features/ens/ensService";
import {UserFlowDemo} from "./UserFlowDemo";
import {ENS_ALERT_REFRESH_INTERVAL_MS} from "../utils";

const LIST_LIMIT = 50;

const FLOW_LEVELS = [
    {level: 1, label: 'L1 · Push'},
    {level: 2, label: 'L2 · ASSIST'},
    {level: 3, label: 'L3 · SMS'},
    {level: 4, label: 'L4 · Phone'},
];

const BellIcon = ({size = 12, color = "#fff"}: { size?: number; color?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
         width={size} height={size}>
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
);

const truncate = (text: string, max: number) => (text.length > max ? `${text.substring(0, max)}…` : text);

// ─── Mobile lockscreen with banner notification ───────────────────
const MobileLockscreenPreview = ({alertTitle, alertMessage}: { alertTitle: string; alertMessage: string }) => (
    <div style={{width: 220, height: 440, borderRadius: 32, background: '#000', boxShadow: '0 20px 50px rgba(0,0,0,0.35), inset 0 0 0 2px #2a2a2e', overflow: 'hidden', position: 'relative', flexShrink: 0}}>
        <div style={{position: 'absolute', inset: 0, background: 'linear-gradient(160deg, #1e1b4b 0%, #312e81 35%, #4c1d95 70%, #6b21a8 100%)'}}>
            <div style={{position: 'absolute', top: '20%', left: '-10%', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(rgba(167,139,250,0.25), transparent 70%)'}}/>
            <div style={{position: 'absolute', bottom: '10%', right: '-15%', width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(rgba(192,132,252,0.2), transparent 70%)'}}/>
        </div>
        <div style={{position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)', width: 80, height: 22, background: '#000', borderRadius: 14, zIndex: 30}}/>
        <div style={{position: 'relative', zIndex: 10, height: 32, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '0 22px 6px'}}>
            <span style={{fontSize: 12, fontWeight: 600, color: '#fff'}}>9:41</span>
            <div style={{display: 'flex', gap: 4, alignItems: 'center'}}>
                <svg viewBox="0 0 17 12" fill="#fff" width="13" height="9"><rect x="0" y="6" width="3" height="6" rx="1"/><rect x="4.5" y="4" width="3" height="8" rx="1"/><rect x="9" y="2" width="3" height="10" rx="1"/><rect x="13.5" y="0" width="3" height="12" rx="1"/></svg>
                <div style={{width: 20, height: 10, borderRadius: 2, border: '1.2px solid rgba(255,255,255,0.7)', position: 'relative', overflow: 'hidden'}}>
                    <div style={{position: 'absolute', left: 1, top: 1, bottom: 1, borderRadius: 1, background: '#fff', width: '75%'}}/>
                </div>
            </div>
        </div>
        <div style={{position: 'relative', zIndex: 5, padding: '24px 0 0', textAlign: 'center'}}>
            <div style={{fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.85)', marginBottom: 2}}>Wednesday, May 6</div>
            <div style={{fontSize: 64, fontWeight: 200, color: '#fff', letterSpacing: '-2px', lineHeight: 1}}>9:41</div>
        </div>
        <div style={{position: 'absolute', top: 168, left: 8, right: 8, zIndex: 20, animation: 'ensLockSlideDown 0.6s cubic-bezier(0.34,1.56,0.64,1) forwards'}}>
            <div style={{background: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderRadius: 14, padding: '10px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
                    <div style={{width: 24, height: 24, borderRadius: 6, background: 'radial-gradient(rgb(142,81,255) 0%, rgb(127,34,254) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                        <BellIcon size={12}/>
                    </div>
                    <div style={{flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <span style={{fontSize: 10, fontWeight: 700, color: 'rgb(24,24,27)', letterSpacing: '0.02em'}}>PHONEHQ</span>
                        <span style={{fontSize: 10, color: 'rgb(113,113,123)'}}>now</span>
                    </div>
                </div>
                <div style={{fontSize: 12, fontWeight: 700, color: 'rgb(24,24,27)', marginBottom: 3, lineHeight: '1.25'}}>{truncate(alertTitle, 32)}</div>
                <div style={{fontSize: 11, color: 'rgb(63,63,71)', lineHeight: '1.35'}}>{truncate(alertMessage, 70)}</div>
            </div>
        </div>
        <div style={{position: 'absolute', bottom: 8, left: 0, right: 0, zIndex: 10, display: 'flex', justifyContent: 'center'}}>
            <div style={{width: 110, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.5)'}}/>
        </div>
    </div>
);

// ─── Desktop macOS-style notification toast ───────────────────────
const DesktopNotificationPreview = ({alertTitle, alertMessage}: {
    alertTitle: string;
    alertMessage: string;
}) => (
    <div style={{width: 480, height: 300, borderRadius: 12, background: 'linear-gradient(160deg, #1e293b 0%, #0f172a 100%)', boxShadow: '0 20px 50px rgba(0,0,0,0.4)', overflow: 'hidden', position: 'relative', flexShrink: 0}}>
        <div style={{position: 'absolute', top: '-30%', right: '-20%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(rgba(99,102,241,0.4), transparent 60%)'}}/>
        <div style={{position: 'absolute', bottom: '-20%', left: '-10%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(rgba(168,85,247,0.3), transparent 60%)'}}/>
        <div style={{position: 'relative', zIndex: 5, height: 24, background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(20px)', display: 'flex', alignItems: 'center', padding: '0 12px', gap: 14}}>
            <span style={{fontSize: 11, color: '#fff', fontWeight: 600}}>Chrome</span>
            <span style={{fontSize: 11, color: 'rgba(255,255,255,0.7)'}}>File</span>
            <span style={{fontSize: 11, color: 'rgba(255,255,255,0.7)'}}>Edit</span>
            <span style={{fontSize: 11, color: 'rgba(255,255,255,0.7)'}}>View</span>
            <div style={{flex: 1}}/>
            <span style={{fontSize: 11, color: 'rgba(255,255,255,0.85)'}}>Wed 9:41 AM</span>
        </div>
        <div style={{position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', height: 38, padding: '0 8px', borderRadius: 12, background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(20px)', display: 'flex', alignItems: 'center', gap: 6, border: '1px solid rgba(255,255,255,0.15)'}}>
            {['#3b82f6', '#ec4899', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'].map((c, i) => (
                <div key={i} style={{width: 28, height: 28, borderRadius: 6, background: c, opacity: 0.85}}/>
            ))}
        </div>
        <div style={{position: 'absolute', top: 36, right: 12, width: 320, animation: 'ensDesktopSlideIn 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards', zIndex: 20}}>
            <div style={{background: 'rgba(38,38,42,0.92)', backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)', borderRadius: 14, padding: '12px 14px', boxShadow: '0 16px 40px rgba(0,0,0,0.5), inset 0 0 0 0.5px rgba(255,255,255,0.1)'}}>
                <div style={{display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10}}>
                    <div style={{width: 32, height: 32, borderRadius: 8, background: 'radial-gradient(rgb(142,81,255) 0%, rgb(127,34,254) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 12px rgba(127,34,254,0.4)'}}>
                        <BellIcon size={16}/>
                    </div>
                    <div style={{flex: 1, minWidth: 0}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3}}>
                            <span style={{fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: '0.02em'}}>PhoneHQ Emergency</span>
                            <span style={{fontSize: 10, color: 'rgba(255,255,255,0.5)'}}>now</span>
                        </div>
                        <div style={{fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 3, lineHeight: '1.3'}}>{truncate(alertTitle, 36)}</div>
                        <div style={{fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: '1.4'}}>{truncate(alertMessage, 90)}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const LEVEL_LABELS: Record<number, string> = {
    1: 'L1 · Push notification',
    2: 'L2 · ASSIST call',
    3: 'L3 · SMS',
    4: 'L4 · Phone call',
};

// ─── Recipient acknowledgement panel (live countdown) ─────────────
const AcknowledgementPanel = ({alertTitle, alertMessage}: {
    alertTitle: string;
    alertMessage: string;
}) => {
    const [acknowledged, setAcknowledged] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(28);

    useEffect(() => {
        if (acknowledged) return;
        const timer = setInterval(() => setTimeRemaining((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(timer);
    }, [acknowledged]);

    const currentLevel = 1;
    const nextLevel = 2;
    const nextLabel = LEVEL_LABELS[nextLevel];
    const urgent = timeRemaining < 10;

    if (acknowledged) {
        return (
            <div style={{borderRadius: 14, background: '#fff', border: '1px solid #86efac', padding: '20px', boxShadow: '0 4px 16px rgba(0,201,80,0.12)'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14}}>
                    <div style={{width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #22c55e, #15803d)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,201,80,0.3)', animation: 'ensAckPulse 0.5s ease-out'}}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <div>
                        <div style={{fontSize: 16, fontWeight: 700, color: 'rgb(24,24,27)', marginBottom: 2}}>Receipt confirmed</div>
                        <div style={{fontSize: 12, color: '#15803d', fontWeight: 600}}>Escalation paused · admin notified</div>
                    </div>
                </div>
                <div style={{padding: '12px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #bbf7d0'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        <span style={{fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.06em'}}>Escalation halted</span>
                    </div>
                    <div style={{fontSize: 12, color: 'rgb(24,24,27)', lineHeight: '1.5'}}>You will not receive further notifications for this alert. The {nextLabel} escalation has been cancelled.</div>
                </div>
            </div>
        );
    }

    return (
        <div style={{borderRadius: 14, background: '#fff', border: '1px solid rgb(230,230,230)', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.08)'}}>
            <div style={{background: 'linear-gradient(90deg, #7c3aed 0%, #a855f7 100%)', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <div style={{width: 8, height: 8, borderRadius: '50%', background: '#fff', animation: 'ensUrgentBlink 1.2s ease-in-out infinite'}}/>
                    <span style={{fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: '0.08em', textTransform: 'uppercase'}}>Action required</span>
                </div>
                <span style={{fontSize: 11, color: 'rgba(255,255,255,0.85)', fontWeight: 600}}>{LEVEL_LABELS[currentLevel]}</span>
            </div>
            <div style={{padding: '16px'}}>
                <div style={{fontSize: 15, fontWeight: 700, color: 'rgb(24,24,27)', marginBottom: 6, lineHeight: '1.3'}}>{alertTitle}</div>
                <div style={{fontSize: 13, color: 'rgb(63,63,71)', lineHeight: '1.5', marginBottom: 14}}>{alertMessage}</div>
                <div style={{marginBottom: 14, padding: '10px 12px', borderRadius: 10, background: urgent ? '#fef2f2' : '#fffbeb', border: `1px solid ${urgent ? '#fecaca' : '#fde68a'}`}}>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                            <svg viewBox="0 0 24 24" fill="none" stroke={urgent ? '#be123c' : '#b45309'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            <span style={{fontSize: 11, fontWeight: 700, color: urgent ? '#be123c' : '#b45309', textTransform: 'uppercase', letterSpacing: '0.04em'}}>Escalates in {timeRemaining}s</span>
                        </div>
                        <span style={{fontSize: 11, color: urgent ? '#be123c' : '#b45309', fontWeight: 600}}>→ {nextLabel}</span>
                    </div>
                    <div style={{height: 4, borderRadius: 2, background: urgent ? '#fecaca' : '#fde68a', overflow: 'hidden'}}>
                        <div style={{height: '100%', width: `${(timeRemaining / 30) * 100}%`, background: urgent ? '#dc2626' : '#d97706', borderRadius: 2, transition: 'width 1s linear'}}/>
                    </div>
                </div>
                <div style={{marginBottom: 14, padding: '12px', borderRadius: 10, background: '#fafafa', border: '1px solid rgb(230,230,230)'}}>
                    <div style={{fontSize: 11, fontWeight: 700, color: 'rgb(113,113,123)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8}}>By confirming, you will:</div>
                    <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
                        {[
                            'Pause the escalation chain for your account',
                            'Notify the admin of your acknowledgement',
                            'Log a confirmation entry to the audit trail',
                        ].map((text) => (
                            <div key={text} style={{display: 'flex', alignItems: 'flex-start', gap: 8}}>
                                <div style={{width: 14, height: 14, borderRadius: '50%', background: '#9747ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1}}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" width="9" height="9"><polyline points="20 6 9 17 4 12"/></svg>
                                </div>
                                <span style={{fontSize: 12, color: 'rgb(24,24,27)', lineHeight: '1.4'}}>{text}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div style={{display: 'flex'}}>
                    <button onClick={() => setAcknowledged(true)} style={{flex: 1, height: 40, borderRadius: 10, border: 'none', background: 'radial-gradient(rgb(142,81,255) 0%, rgb(127,34,254) 100%)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, boxShadow: '0 4px 12px rgba(127,34,254,0.3)'}}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><polyline points="20 6 9 17 4 12"/></svg>
                        Confirm receipt &amp; pause escalation
                    </button>
                </div>
            </div>
        </div>
    );
};

const SectionHeading = ({title, subtitle}: { title: string; subtitle: string }) => (
    <div>
        <div className="text-[13px] font-bold text-ink">{title}</div>
        <div className="text-[11px] text-ink-muted">{subtitle}</div>
    </div>
);

export const EnsNotifications = () => {
    const {data, isLoading} = useListEnsAlertsQuery(
        {offset: 0, limit: LIST_LIMIT},
        {pollingInterval: ENS_ALERT_REFRESH_INTERVAL_MS},
    );
    const alerts = data?.data ?? [];

    const [selectedPublicId, setSelectedPublicId] = useState<string | null>(null);
    const [demoKey, setDemoKey] = useState(0);
    const [flowLevel, setFlowLevel] = useState(1);

    const selected = alerts.find((a) => a.publicId === selectedPublicId) ?? alerts[0];

    const reset = () => {
        setDemoKey((k) => k + 1);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <PhoneHQLoader/>
            </div>
        );
    }

    if (!selected) {
        return (
            <div className="px-5 py-16 text-center text-sm text-ink-muted">
                Create an alert to preview how recipients receive it.
            </div>
        );
    }

    const title = selected.name;
    const message = selected.message;
    return (
        <div className="flex flex-col">
            <style>{`
                @keyframes ensLockSlideDown { from { opacity: 0; transform: translateY(-30px) scale(0.94); } to { opacity: 1; transform: translateY(0) scale(1); } }
                @keyframes ensDesktopSlideIn { from { opacity: 0; transform: translateX(40px); } to { opacity: 1; transform: translateX(0); } }
                @keyframes ensUrgentBlink { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
                @keyframes ensAckPulse { from { transform: scale(0.6); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                @keyframes ensFlowSlideDown { from { opacity:0; transform:translateY(-20px) scale(0.96); } to { opacity:1; transform:translateY(0) scale(1); } }
                @keyframes ensFlowPulse { 0%,100%{box-shadow:0 0 0 10px rgba(239,68,68,0.15),0 0 0 20px rgba(239,68,68,0.07)} 50%{box-shadow:0 0 0 14px rgba(239,68,68,0.2),0 0 0 26px rgba(239,68,68,0.04)} }
                @keyframes ensFlowPopIn { from{transform:scale(0);opacity:0} to{transform:scale(1);opacity:1} }
            `}</style>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
                <SectionHeading title="Notification preview" subtitle="How recipients see this alert across devices"/>
                <div className="flex-1"/>
                <select
                    value={selected.publicId}
                    onChange={(e) => {
                        setSelectedPublicId(e.target.value);
                        reset();
                    }}
                    className="h-8 min-w-[280px] max-w-full rounded-lg border border-line bg-surface px-2.5 text-xs text-ink outline-none"
                >
                    {alerts.map((a) => (
                        <option key={a.publicId} value={a.publicId}>
                            {truncate(`${a.publicId} — ${a.name}`, 56)}
                        </option>
                    ))}
                </select>
                <button
                    type="button"
                    onClick={reset}
                    className="flex h-8 items-center gap-1.5 rounded-lg border border-line bg-surface px-3 text-xs font-semibold text-ink hover:border-brand-focus hover:text-brand-ink"
                >
                    <RotateCcw size={13} className="!p-0"/>
                    Reset
                </button>
            </div>

            {/* Body */}
            <div className="bg-surface-muted px-5 py-6">
                <div className="mb-6 flex flex-col gap-2.5">
                    <SectionHeading
                        title="L1 · Initial push notification"
                        subtitle="The first touchpoint — push to mobile lockscreen and desktop OS notification."
                    />
                    <div
                        key={demoKey}
                        className="flex flex-wrap items-center justify-center gap-8 rounded-2xl border border-line bg-surface px-5 py-8"
                    >
                        <div className="flex flex-col items-center gap-3">
                            <MobileLockscreenPreview alertTitle={title} alertMessage={message}/>
                            <span className="rounded-full bg-surface-muted px-2.5 py-1 text-[11px] font-semibold text-ink">
                                Mobile · Lockscreen
                            </span>
                        </div>
                        <div className="flex flex-col items-center gap-3">
                            <DesktopNotificationPreview
                                alertTitle={title}
                                alertMessage={message}
                            />
                            <span className="rounded-full bg-surface-muted px-2.5 py-1 text-[11px] font-semibold text-ink">
                                Desktop · OS toast
                            </span>
                        </div>
                    </div>
                </div>

                <div className="mb-6 flex flex-col gap-2.5">
                    <SectionHeading
                        title="Acknowledgement panel"
                        subtitle="Confirmation surface that opens when a recipient taps the notification. Confirming halts the escalation chain."
                    />
                    <div className="mx-auto w-full max-w-[540px]" key={`ack-${demoKey}`}>
                        <AcknowledgementPanel alertTitle={title} alertMessage={message}/>
                    </div>
                </div>

                <div className="flex flex-col gap-2.5">
                    <SectionHeading
                        title="User experience"
                        subtitle="Walk through how a recipient confirms the alert at each escalation level on their phone."
                    />
                    <div className="rounded-2xl border border-line bg-surface px-5 py-6">
                        <div className="mb-5 flex flex-wrap items-center justify-center gap-2">
                            {FLOW_LEVELS.map((option) => (
                                <button
                                    key={option.level}
                                    type="button"
                                    onClick={() => {
                                        setFlowLevel(option.level);
                                        setDemoKey((k) => k + 1);
                                    }}
                                    className={`h-8 rounded-full px-3 text-xs font-semibold transition-colors ${
                                        flowLevel === option.level
                                            ? 'bg-inverse-surface text-white'
                                            : 'bg-surface-muted text-ink-secondary hover:bg-surface-muted'
                                    }`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                        <div className="flex justify-center" key={`flow-${flowLevel}-${demoKey}`}>
                            <UserFlowDemo alertTitle={title} alertMessage={message} startLevel={flowLevel}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
